import { useState, useRef, useEffect } from "react";
import { T } from "../data/Tokens";
import { apiFetch } from "../../api/api";

const BG_COLORS = ["#D8D5D0", "#1A1A1A", "#2C4A3E", "#E8D4D4", "#D4D4E8", "#F0E6D3", "#FFFFFF", "#F5F0E8"];

const FONTS = [
  "Georgia", "Times New Roman", "Arial", "Helvetica",
  "Playfair Display", "Cormorant Garamond", "Cormorant", "EB Garamond", "Libre Baskerville",
  "Crimson Text", "Marcellus", "Cinzel", "Merriweather",
  "Inter", "Roboto", "Open Sans", "Montserrat", "Lato", "Poppins", "Raleway",
  "Nunito", "Quicksand", "Josefin Sans", "Oswald", "Bebas Neue", "Abril Fatface",
  "Dancing Script", "Caveat", "Great Vibes", "Pacifico",
];

// ── Background image upload helper ──
const UPLOAD_PATH = "/products/upload-image.php";

async function uploadImageFile(file) {
  const formData = new FormData();
  formData.append("image", file);
  const data = await apiFetch(UPLOAD_PATH, { method: "POST", body: formData });
  return data.data.url;
}

// ── Back Cover aur Front Cover — hamesha fixed, sabse pehle, koi default text nahi ──
const makeFixedCoverPages = () => ([
  { pageType: "back",  bgColor: "#D8D5D0", bgImage: null, photos: [], texts: [] },
  { pageType: "front", bgColor: "#D8D5D0", bgImage: null,
    photos: [{ id: Date.now(), x: 10, y: 10, w: 80, h: 55, rotate: 0 }], texts: [] },
]);

// ── Extra normal page — koi default text nahi ──
const makeNormalPage = () => ({
  pageType: "normal", bgColor: "#FFFFFF", bgImage: null,
  photos: [{ id: Date.now(), x: 20, y: 20, w: 60, h: 60, rotate: 0 }],
  texts: [],
});

// ── Empty image icon — light gray placeholder ──
function ImagePlaceholderIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.3" className="w-8 h-8 opacity-70">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

// ── Generic drag/resize box logic — reused by both Photo aur Text slots ──
function useDragResize(item, onChange, containerRef) {
  const setItem = (updater) => onChange(item.id, updater);
  const dragState = useRef(null);
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const startDrag = (e, mode, handle) => {
    e.preventDefault(); e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    dragState.current = { mode, handle, startX: e.clientX, startY: e.clientY, startItem: { ...item }, rectW: rect.width, rectH: rect.height, rectLeft: rect.left, rectTop: rect.top };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
  };

  const onDrag = (e) => {
    const ds = dragState.current;
    if (!ds) return;
    const dxPct = ((e.clientX - ds.startX) / ds.rectW) * 100;
    const dyPct = ((e.clientY - ds.startY) / ds.rectH) * 100;
    if (ds.mode === "move") {
      const x = clamp(ds.startItem.x + dxPct, 0, 100 - ds.startItem.w);
      const y = clamp(ds.startItem.y + dyPct, 0, 100 - ds.startItem.h);
      setItem(prev => ({ ...prev, x, y }));
    } else if (ds.mode === "resize") {
      let { x, y, w, h } = ds.startItem;
      const handle = ds.handle;
      if (handle.includes("e")) w = clamp(ds.startItem.w + dxPct, 5, 100 - x);
      if (handle.includes("s")) h = clamp(ds.startItem.h + dyPct, 3, 100 - y);
      if (handle.includes("w")) { const newX = clamp(ds.startItem.x + dxPct, 0, ds.startItem.x + ds.startItem.w - 5); w = ds.startItem.w + (ds.startItem.x - newX); x = newX; }
      if (handle.includes("n")) { const newY = clamp(ds.startItem.y + dyPct, 0, ds.startItem.y + ds.startItem.h - 3); h = ds.startItem.h + (ds.startItem.y - newY); y = newY; }
      setItem(prev => ({ ...prev, x, y, w, h }));
    } else if (ds.mode === "rotate") {
      const cx = ds.rectLeft + ((ds.startItem.x + ds.startItem.w / 2) / 100) * ds.rectW;
      const cy = ds.rectTop + ((ds.startItem.y + ds.startItem.h / 2) / 100) * ds.rectH;
      const angle = Math.round(Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90);
      setItem(prev => ({ ...prev, rotate: angle }));
    }
  };

  const stopDrag = () => {
    dragState.current = null;
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", stopDrag);
  };

  return { startDrag };
}

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const CURSOR_FOR = { nw: "nwse-resize", se: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize", n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize" };
const POS_FOR = { nw: { left: "0%", top: "0%" }, n: { left: "50%", top: "0%" }, ne: { left: "100%", top: "0%" }, e: { left: "100%", top: "50%" }, se: { left: "100%", top: "100%" }, s: { left: "50%", top: "100%" }, sw: { left: "0%", top: "100%" }, w: { left: "0%", top: "50%" } };

function PhotoSlotEditor({ slot, onChange, containerRef }) {
  const { startDrag } = useDragResize(slot, onChange, containerRef);
  return (
    <div className="absolute flex items-center justify-center select-none"
      style={{ left: `${slot.x}%`, top: `${slot.y}%`, width: `${slot.w}%`, height: `${slot.h}%`, transform: `rotate(${slot.rotate}deg)`, background: "#E0E0E0", outline: "2px dashed #8b5cf6", boxSizing: "border-box", zIndex: 10, cursor: "move" }}
      onMouseDown={(e) => startDrag(e, "move")}>
      <ImagePlaceholderIcon />
      {HANDLES.map(h => (
        <div key={h} onMouseDown={(e) => startDrag(e, "resize", h)}
          className="absolute bg-white border-2 border-purple-500 rounded-full"
          style={{ width: 11, height: 11, left: POS_FOR[h].left, top: POS_FOR[h].top, transform: "translate(-50%, -50%)", cursor: CURSOR_FOR[h] }} />
      ))}
      <div onMouseDown={(e) => startDrag(e, "rotate")}
        className="absolute bg-white border-2 border-purple-500 rounded-full flex items-center justify-center text-[9px]"
        style={{ width: 16, height: 16, left: "50%", top: -28, transform: "translate(-50%, -50%)", cursor: "grab" }}>↻</div>
      <div className="absolute bg-purple-500" style={{ width: 1, height: 18, left: "50%", top: -18, transform: "translateX(-50%)" }} />
    </div>
  );
}

// ── Text Slot — ab bilkul Photo Slot jaisa hi: always-visible outline, 8 resize handles ──
function TextSlotEditor({ slot, onChange, containerRef, onSelect }) {
  const { startDrag } = useDragResize(slot, onChange, containerRef);
  return (
    <div className="absolute flex items-center justify-center select-none overflow-hidden"
      style={{ left: `${slot.x}%`, top: `${slot.y}%`, width: `${slot.w}%`, height: `${slot.h}%`, background: "rgba(139,92,246,0.06)", outline: "2px dashed #8b5cf6", boxSizing: "border-box", zIndex: 15, cursor: "move" }}
      onMouseDown={(e) => { onSelect && onSelect(); startDrag(e, "move"); }}>
      <div style={{ color: slot.color || "#1A1A1A", fontSize: slot.size || "16px", fontFamily: slot.font || "Georgia", textAlign: slot.align || "center", pointerEvents: "none", lineHeight: 1.2, width: "100%", padding: "0 2px" }}>
        {slot.content || "Text"}
      </div>
      {HANDLES.map(h => (
        <div key={h} onMouseDown={(e) => { onSelect && onSelect(); startDrag(e, "resize", h); }}
          className="absolute bg-white border-2 border-purple-500 rounded-full"
          style={{ width: 11, height: 11, left: POS_FOR[h].left, top: POS_FOR[h].top, transform: "translate(-50%, -50%)", cursor: CURSOR_FOR[h] }} />
      ))}
    </div>
  );
}

// ── Color picker: swatches + custom picker + hex text input — reused for bg AND text color ──
function ColorPicker({ value, onChange }) {
  const [hexInput, setHexInput] = useState(value || "");
  useEffect(() => { setHexInput(value || ""); }, [value]);

  const commitHex = () => {
    let v = hexInput.trim();
    if (!v) return;
    if (!v.startsWith("#")) v = "#" + v;
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v)) onChange(v);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 items-center mb-1.5">
        {BG_COLORS.map(c => (
          <button key={c} onClick={() => onChange(c)}
            className="w-6 h-6 rounded-full border-2 transition-all"
            style={{ background: c, borderColor: value === c ? T.gold : T.border }} />
        ))}
        <input type="color" value={value || "#FFFFFF"} onChange={e => onChange(e.target.value)}
          className="w-6 h-6 rounded-lg border cursor-pointer p-0.5" style={{ borderColor: T.border }} />
      </div>
      <div className="flex items-center gap-1.5">
        <input value={hexInput} onChange={e => setHexInput(e.target.value)}
          onBlur={commitHex} onKeyDown={e => e.key === "Enter" && commitHex()}
          placeholder="#999999"
          className="px-2 py-1 rounded-lg text-[11px] outline-none border w-24" style={{ borderColor: T.border }} />
        <div className="w-5 h-5 rounded border flex-shrink-0" style={{ background: value, borderColor: T.border }} />
      </div>
    </div>
  );
}

export default function PhotobooksAdmin() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [linkProductUrl, setLinkProductUrl] = useState("");

  const [draftName, setDraftName] = useState("");
  const [pages, setPages] = useState(makeFixedCoverPages());
  const [activePage, setActivePage] = useState(1); // default: Front Cover tab open
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef();
  const bgImgRef = useRef();

  const cur = pages[activePage];
  const setCur = (updater) => setPages(prev => prev.map((p, i) => i === activePage ? { ...p, ...(typeof updater === "function" ? updater(p) : updater) } : p));

  const fetchTemplates = () => {
    setLoading(true);
    apiFetch("/photobook/list.php?admin=true")
      .then(data => setTemplates(data.data || []))
      .catch(err => setError(err.message || "Failed to load templates"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchTemplates(); }, []);

  const resetDraft = () => {
    setDraftName(""); setPages(makeFixedCoverPages()); setActivePage(1);
    setEditingId(null); setLinkProductUrl(""); setSelectedTextId(null);
  };

  const addPage = () => { setPages(prev => [...prev, makeNormalPage()]); setActivePage(pages.length); };
  const deletePage = (idx) => {
    if (idx === 0 || idx === 1) return; // Front/Back Cover delete nahi ho sakte
    setPages(prev => prev.filter((_, i) => i !== idx));
    setActivePage(prev => (idx <= prev ? Math.max(0, prev - 1) : prev));
  };

  const addPhotoSlot = () => setCur(p => ({ photos: [...p.photos, { id: Date.now(), x: 15, y: 15, w: 40, h: 40, rotate: 0 }] }));
  const updatePhotoSlot = (id, updater) => setCur(p => ({ photos: p.photos.map(ph => ph.id === id ? { ...ph, ...(typeof updater === "function" ? updater(ph) : updater) } : ph) }));
  const removePhotoSlot = (id) => setCur(p => ({ photos: p.photos.filter(ph => ph.id !== id) }));

  const addText = () => {
    const id = Date.now();
    setCur(p => ({ texts: [...p.texts, { id, x: 5, y: 65, w: 90, h: 15, content: "YOUR TEXT HERE", font: "Georgia", size: "20px", color: "#1A1A1A", align: "center" }] }));
    setSelectedTextId(id);
  };
  const updateTextById = (id, updater) => setCur(p => ({ texts: p.texts.map(t => t.id === id ? { ...t, ...(typeof updater === "function" ? updater(t) : updater) } : t) }));
  const removeText = (id) => { setCur(p => ({ texts: p.texts.filter(t => t.id !== id) })); if (selectedTextId === id) setSelectedTextId(null); };

  // ── Background image upload handler ──
  const handleBgImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const previewUrl = URL.createObjectURL(file);
    setCur({ bgImage: { url: previewUrl, uploading: true } });
    uploadImageFile(file)
      .then(url => setCur({ bgImage: { url, uploading: false } }))
      .catch(err => setCur({ bgImage: { url: previewUrl, uploading: false, error: err.message || "Upload failed" } }));
  };

  const saveTemplate = async () => {
    if (!draftName.trim()) { alert("Design ka naam daalo."); return; }
    if (cur.bgImage?.uploading) { alert("Background image abhi upload ho rahi hai, thoda wait karo."); return; }

    setSaving(true);
    try {
      const payload = {
        name: draftName.trim(),
        pages: pages.map(p => ({
          isCover: p.pageType === "front" || p.pageType === "back",
          coverSide: p.pageType === "front" ? "front" : p.pageType === "back" ? "back" : null,
          bgColor: p.bgColor || "#FFFFFF",
          bgImageUrl: p.bgImage?.url || null,
          photos: p.photos,
          texts: p.texts, // agar empty array hai to koi text pass nahi hoga
        })),
      };
      let savedTemplateId = editingId;
      if (editingId) {
        await apiFetch("/photobook/update.php", { method: "PUT", body: { id: editingId, ...payload } });
      } else {
        const res = await apiFetch("/photobook/create.php", { method: "POST", body: payload });
        savedTemplateId = res.data.id;
      }

      if (linkProductUrl.trim()) {
        try {
          await apiFetch("/products/link-photobook.php", { method: "POST", body: { slugOrUrl: linkProductUrl.trim(), templateId: savedTemplateId } });
        } catch (linkErr) {
          alert("Design save ho gaya, lekin product link karne mein error: " + (linkErr.message || ""));
        }
      }

      resetDraft();
      fetchTemplates();
    } catch (err) {
      alert(err.message || "Design save nahi hua.");
    } finally {
      setSaving(false);
    }
  };

  const loadForEdit = async (t) => {
    setEditingId(t.id);
    setDraftName(t.name);
    const back = t.pages?.find(pg => pg.coverSide === "back") || {};
    const front = t.pages?.find(pg => pg.coverSide === "front") || {};
    const normals = (t.pages || []).filter(pg => pg.coverSide !== "back" && pg.coverSide !== "front");
    const mapPage = (pg, type) => ({
      pageType: type,
      bgColor: pg.bgColor || (type === "normal" ? "#FFFFFF" : "#D8D5D0"),
      bgImage: pg.bgImageUrl ? { url: pg.bgImageUrl, uploading: false } : null,
      photos: pg.photos || [],
      texts: (pg.texts || []).map((t, i) => ({ id: t.id || Date.now() + i, h: t.h || 15, ...t })),
    });
    setPages([
      mapPage(back, "back"),
      mapPage(front, "front"),
      ...normals.map(pg => mapPage(pg, "normal")),
    ]);
    setActivePage(1);
    setSelectedTextId(null);
    setLinkProductUrl("");
    try {
      const res = await apiFetch(`/products/find-by-template.php?templateId=${t.id}`);
      if (res.data?.slug) setLinkProductUrl(res.data.slug);
    } catch { /* ignore */ }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renameTemplate = async (id, name) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, name } : t));
    try { await apiFetch("/photobook/update.php", { method: "PUT", body: { id, name } }); }
    catch (err) { alert(err.message || "Rename fail ho gaya."); fetchTemplates(); }
  };

  const toggleStatus = async (t) => {
    const nextStatus = t.status === "Active" ? "Inactive" : "Active";
    setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, status: nextStatus } : x));
    try { await apiFetch("/photobook/update.php", { method: "PUT", body: { id: t.id, status: nextStatus } }); }
    catch (err) { alert(err.message || "Status update fail ho gaya."); fetchTemplates(); }
  };

  const deleteTemplate = async (id) => {
    if (!confirm("Delete this design?")) return;
    const prevTemplates = templates;
    setTemplates(prev => prev.filter(t => t.id !== id));
    try { await apiFetch("/photobook/delete.php", { method: "DELETE", body: { id } }); }
    catch (err) { alert(err.message || "Delete fail ho gaya."); setTemplates(prevTemplates); }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-[18px] sm:text-[24px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Photobook Page Designs</h1>
        <p className="text-[11px] sm:text-[12px]" style={{ color: T.textSec }}>{templates.length} designs</p>
      </div>

      <div className="rounded-2xl p-3 sm:p-4" style={{ background: "#fff", border: `1px solid ${T.border}` }}>
        <p className="text-[12px] sm:text-[13px] font-semibold mb-2.5" style={{ color: T.textPri }}>
          {editingId ? "Edit Page Design" : "Create New Page Design"}
        </p>

        <input value={draftName} onChange={e => setDraftName(e.target.value)}
          placeholder="Design name (e.g. Golden Days Wedding Album)"
          className="w-full mb-3 px-3 py-2 rounded-xl text-[13px] outline-none border" style={{ borderColor: T.border, color: T.textPri }} />

        {/* Tabs: Back Cover, Front Cover (fixed) + extra normal pages + Add Page */}
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1">
          {pages.map((p, i) => (
            <button key={i} onClick={() => { setActivePage(i); setSelectedTextId(null); }}
              className="flex-shrink-0 px-3.5 py-1.5 rounded-lg text-[11px] font-semibold border relative group"
              style={{ borderColor: i === activePage ? T.gold : T.border, background: i === activePage ? T.gold : "#fff", color: i === activePage ? "#fff" : T.textPri }}>
              {p.pageType === "front" ? "Front Cover" : p.pageType === "back" ? "Back Cover" : `Page ${i - 1}`}
              {i > 1 && (
                <span onClick={(e) => { e.stopPropagation(); deletePage(i); }}
                  className="ml-1.5 opacity-60 hover:opacity-100">✕</span>
              )}
            </button>
          ))}
          <button onClick={addPage} className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold border" style={{ borderColor: T.gold, color: T.gold }}>
            + Add Page
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="space-y-3">

            <div>
              <p className="text-[10.5px] sm:text-[11px] font-semibold mb-1.5" style={{ color: T.textPri }}>Background Color</p>
              <ColorPicker value={cur.bgColor} onChange={c => setCur({ bgColor: c })} />
            </div>

            <div>
              <p className="text-[10.5px] sm:text-[11px] font-semibold mb-1.5" style={{ color: T.textPri }}>Background Image (optional)</p>
              <div onClick={() => bgImgRef.current.click()}
                className="rounded-xl border-2 border-dashed p-2 text-center cursor-pointer relative overflow-hidden"
                style={{ borderColor: T.border, minHeight: 56, background: T.bgSoft }}>
                {cur.bgImage?.url ? (
                  <>
                    <img src={cur.bgImage.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {cur.bgImage.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.7)" }}>
                        <div style={{ width: 16, height: 16, border: "3px solid rgba(200,164,106,0.3)", borderTopColor: T.gold, borderRadius: "50%", animation: "pbspin 0.7s linear infinite" }} />
                        <style>{`@keyframes pbspin{to{transform:rotate(360deg)}}`}</style>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.5)" }}>
                      <button onClick={(e) => { e.stopPropagation(); bgImgRef.current.click(); }} className="text-white text-[10px] font-semibold px-2 py-1 rounded bg-black/40 hover:bg-black/60">Change</button>
                      <button onClick={(e) => { e.stopPropagation(); setCur({ bgImage: null }); }} className="text-white text-[10px] font-semibold px-2 py-1 rounded bg-red-500/70 hover:bg-red-500">Remove</button>
                    </div>
                  </>
                ) : <p className="text-[10px] sm:text-[11px] mt-2" style={{ color: T.textSec }}>Click to upload background image</p>}
                <input ref={bgImgRef} type="file" accept="image/*" className="hidden" onChange={e => { handleBgImageUpload(e.target.files[0]); e.target.value = ""; }} />
              </div>
              {cur.bgImage?.error && <p className="text-[10px] mt-1" style={{ color: T.danger }}>{cur.bgImage.error}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <p className="text-[10.5px] sm:text-[11px] font-semibold" style={{ color: T.textPri }}>Photo Slots</p>
                <button onClick={addPhotoSlot} className="text-[10px] font-semibold px-2 py-1 rounded-lg border flex-shrink-0" style={{ borderColor: T.gold, color: T.gold }}>+ Add Photo</button>
              </div>
              <div className="space-y-1.5">
                {cur.photos.map((p, idx) => (
                  <div key={p.id} className="rounded-xl p-2 border" style={{ borderColor: T.border, background: T.bgSoft }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-semibold" style={{ color: T.textSec }}>Photo {idx + 1}</span>
                      <button onClick={() => removePhotoSlot(p.id)} className="text-[10px]" style={{ color: T.danger }}>Remove</button>
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {["x", "y", "w", "h", "rotate"].map(key => (
                        <div key={key}>
                          <label className="text-[8px] sm:text-[9px] uppercase" style={{ color: T.textSec }}>{key}</label>
                          <input type="number" value={p[key]} onChange={e => updatePhotoSlot(p.id, { [key]: parseFloat(e.target.value) || 0 })}
                            className="w-full px-1 py-1 rounded-lg text-[11px] sm:text-[12px] outline-none border" style={{ borderColor: T.border, color: T.textPri }} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {cur.photos.length === 0 && <p className="text-[10px]" style={{ color: T.textSec }}>Koi photo slot nahi (optional).</p>}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <p className="text-[10.5px] sm:text-[11px] font-semibold" style={{ color: T.textPri }}>Text Slots — box ko mouse se drag/resize karo (photo jaisa)</p>
                <button onClick={addText} className="text-[10px] font-semibold px-2 py-1 rounded-lg border flex-shrink-0" style={{ borderColor: T.gold, color: T.gold }}>+ Add Text</button>
              </div>
              <div className="space-y-1.5">
                {cur.texts.map((t) => (
                  <div key={t.id} onClick={() => setSelectedTextId(t.id)}
                    className="rounded-xl p-2 border cursor-pointer"
                    style={{ borderColor: selectedTextId === t.id ? T.gold : T.border, background: T.bgSoft }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-semibold" style={{ color: T.textSec }}>Text — editable on frontend</span>
                      <button onClick={(e) => { e.stopPropagation(); removeText(t.id); }} className="text-[10px]" style={{ color: T.danger }}>Remove</button>
                    </div>
                    <input value={t.content} onChange={e => updateTextById(t.id, { content: e.target.value })} placeholder="Default text (e.g. YOUR FIRST YEAR)"
                      className="w-full mb-1.5 px-2 py-1 rounded-lg text-[11px] outline-none border" style={{ borderColor: T.border }} />
                    <div className="grid grid-cols-2 gap-1 mb-1.5">
                      <select value={t.font || "Georgia"} onChange={e => updateTextById(t.id, { font: e.target.value })}
                        className="px-1 py-1 rounded-lg text-[10px] outline-none border" style={{ borderColor: T.border, fontFamily: t.font || "Georgia" }}>
                        {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                      </select>
                      <select value={t.align || "center"} onChange={e => updateTextById(t.id, { align: e.target.value })}
                        className="px-1 py-1 rounded-lg text-[10px] outline-none border" style={{ borderColor: T.border }}>
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-[9px] flex-shrink-0" style={{ color: T.textSec }}>Font Size</span>
                      <input type="number" value={parseInt(t.size) || 20} onChange={e => updateTextById(t.id, { size: `${e.target.value}px` })}
                        className="w-16 px-1 py-1 rounded-lg text-[10px] outline-none border" style={{ borderColor: T.border }} />
                      <span className="text-[9px]" style={{ color: T.textSec }}>px</span>
                    </div>
                    <p className="text-[9px] mb-1" style={{ color: T.textSec }}>Text Color</p>
                    <ColorPicker value={t.color || "#1A1A1A"} onChange={c => updateTextById(t.id, { color: c })} />
                  </div>
                ))}
                {cur.texts.length === 0 && <p className="text-[10px]" style={{ color: T.textSec }}>Koi text slot nahi — is page pe koi text nahi jayega.</p>}
              </div>
            </div>

            <div>
              <p className="text-[10.5px] sm:text-[11px] font-semibold mb-1.5" style={{ color: T.textPri }}>Link to Product (optional)</p>
              <input value={linkProductUrl} onChange={e => setLinkProductUrl(e.target.value)} placeholder="Paste product URL or slug"
                className="w-full px-3 py-2 rounded-xl text-[12px] sm:text-[13px] outline-none border" style={{ borderColor: T.border, color: T.textPri }} />
              <p className="text-[9px] sm:text-[10px] mt-1" style={{ color: T.textSec }}>Ye poori Design (cover + pages) is product se link hogi.</p>
            </div>
          </div>

          <div>
            <p className="text-[10.5px] sm:text-[11px] font-semibold mb-1.5" style={{ color: T.textPri }}>
              Live Preview — {cur.pageType === "front" ? "Front Cover" : cur.pageType === "back" ? "Back Cover" : `Page ${activePage - 1}`}
            </p>
            <div ref={previewRef} className="relative w-full rounded-xl border overflow-hidden mx-auto"
              style={{ aspectRatio: "4/5", maxWidth: 420, borderColor: T.border, background: cur.bgColor || "#EDECEA" }}>
              {cur.bgImage?.url && <img src={cur.bgImage.url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              {cur.photos.map(p => <PhotoSlotEditor key={p.id} slot={p} onChange={updatePhotoSlot} containerRef={previewRef} />)}
              {cur.texts.map(t => (
                <TextSlotEditor key={t.id} slot={t} onChange={updateTextById} containerRef={previewRef} onSelect={() => setSelectedTextId(t.id)} />
              ))}
            </div>
            <p className="text-[9px] mt-1.5" style={{ color: T.textSec }}>Photo box aur Text box dono ko drag karke move karo, corner/edge dots se size badlo — bilkul same tarike se.</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={saveTemplate} disabled={saving}
            className="flex-1 py-1.5 sm:py-2 rounded-lg text-[12px] sm:text-[13px] font-semibold text-white transition-colors"
            style={{ background: saving ? "#999" : T.gold }}>
            {saving ? "Saving..." : `Save Design (${pages.length} page${pages.length > 1 ? "s" : ""})`}
          </button>
          <button onClick={resetDraft} className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[12px] sm:text-[13px] font-medium border" style={{ borderColor: T.border, color: T.textSec }}>
            {editingId ? "Cancel" : "Clear"}
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl p-3 text-[12px]" style={{ background: "#fee", color: T.danger }}>{error} — <button onClick={fetchTemplates} className="underline">retry</button></div>}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div style={{ width: 24, height: 24, border: "3px solid rgba(200,164,106,0.3)", borderTopColor: T.gold, borderRadius: "50%", animation: "pbspin2 0.7s linear infinite" }} />
          <style>{`@keyframes pbspin2{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl" style={{ background: "#fff", border: `1px solid ${T.border}` }}>
          <div className="text-4xl mb-3">📷</div>
          <p className="text-[14px] font-semibold" style={{ color: T.textPri }}>No designs yet</p>
          <p className="text-[12px] mt-1" style={{ color: T.textSec }}>Create your first page design above</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
          {templates.map(t => {
            const front = t.pages?.find(pg => pg.coverSide === "front") || t.pages?.[0];
            return (
              <div key={t.id} className="rounded-xl sm:rounded-2xl overflow-hidden bg-white" style={{ border: `1px solid ${T.border}`, opacity: t.status === "Inactive" ? 0.55 : 1 }}>
                <div className="relative aspect-square group" style={{ background: front?.bgColor || "#EDECEA" }}>
                  {front?.bgImageUrl && <img src={front.bgImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                  {(front?.photos || []).map((p, i) => (
                    <div key={i} className="absolute flex items-center justify-center" style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${p.w}%`, height: `${p.h}%`, background: "#E0E0E0", transform: `rotate(${p.rotate}deg)` }}>
                      <ImagePlaceholderIcon />
                    </div>
                  ))}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-semibold text-white" style={{ background: "rgba(0,0,0,0.5)" }}>{t.pages?.length || 0}p</div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.45)" }}>
                    <button onClick={() => loadForEdit(t)} className="text-white text-[10px] sm:text-[11px] font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-black/50 hover:bg-black/70">✎ Edit</button>
                  </div>
                </div>
                <div className="p-2 sm:p-2.5">
                  <input value={t.name} onChange={e => renameTemplate(t.id, e.target.value)}
                    className="w-full text-[11px] sm:text-[12px] font-semibold outline-none mb-1.5 px-1 py-0.5 rounded" style={{ color: T.textPri }} />
                  <div className="flex gap-1">
                    <button onClick={() => toggleStatus(t)} className="flex-1 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10.5px] font-medium border"
                      style={{ borderColor: T.border, color: t.status === "Active" ? T.success : T.textSec }}>
                      {t.status === "Active" ? "Active" : "Inactive"}
                    </button>
                    <button onClick={() => deleteTemplate(t.id)} className="px-2 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10.5px] font-medium border" style={{ borderColor: T.border, color: T.danger }}>🗑</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}